/**
 * Nanomaterials Explore (CT-INTERACT): the learner types a particle size and a cut size and the
 * model works out the size class, the surface-area-to-volume ratio and the area factor from the
 * same pure functions the practice cases use (`materialsChemistryNano.ts`).
 */
import { formatSig } from './gasLaws';
import {
  NANOSCALE_MAX_NM,
  NANOSCALE_MIN_NM,
  cubeSurfaceToVolume,
  sizeClass,
  sphereSurfaceToVolume,
  subdivisionAreaFactor,
} from './materialsChemistryNano';
import { firstModelError, type ExploreChallenge, type ModelField, type WorkedStep } from './modelInputContract';

export type NanoShape = 'cube' | 'sphere';

export type NanoInputKey = 'sizeNm' | 'cutNm';

export interface NanoExploreSetup {
  shape: NanoShape;
  /** Cube edge l or sphere radius r, nm. */
  sizeNm: number;
  /** Edge of the small cubes the big cube is cut into, nm (cube only). */
  cutNm: number;
}

export const NANO_SIZE_MIN_NM = 1;
export const NANO_SIZE_MAX_NM = 1e7;
/** Right-hand end of the drawn size scale (10 µm). Larger sizes sit at the end with a note. */
export const NANO_SCALE_END_NM = 1e4;

export function nanoExploreFields(shape: NanoShape): readonly ModelField<NanoInputKey>[] {
  return [
    {
      key: 'sizeNm',
      label: shape === 'cube' ? 'Cube edge L' : 'Sphere radius r',
      unit: 'nm',
      min: NANO_SIZE_MIN_NM,
      max: NANO_SIZE_MAX_NM,
      step: 1,
    },
    { key: 'cutNm', label: 'Cut size l', unit: 'nm', min: NANO_SIZE_MIN_NM, max: NANO_SIZE_MAX_NM, step: 1 },
  ];
}

export const DEFAULT_NANO_SETUP: NanoExploreSetup = { shape: 'cube', sizeNm: 1000, cutNm: 10 };

export interface NanoExploreResult {
  steps: WorkedStep[];
  /** Size used on the size scale: the edge of a cube or the diameter of a sphere. */
  scaleSizeNm: number;
  sizeClassLabel: string;
  ratioPerNm: number;
  /** Cuts per edge (L ÷ l) when the cut is valid, else null. */
  perEdge: number | null;
  limitNote: string | null;
}

const CLASS_TEXT = {
  'below-nanoscale': 'below the nanoscale',
  nanoscale: 'nanoscale',
  'above-nanoscale': 'above the nanoscale',
} as const;

function sig(value: number): string {
  return formatSig(value, 3);
}

/** Whole-number check that tolerates floating-point noise in L ÷ l. */
function isWhole(value: number): boolean {
  return Math.abs(value - Math.round(value)) < 1e-9 * Math.max(1, value);
}

/**
 * Worked nanoscale calculation. Throws the field message for an invalid size or cut. A cut larger
 * than the cube, or one that does not divide the edge into whole cubes, stops at the model limit.
 */
export function calculateNanoExplore(setup: NanoExploreSetup): NanoExploreResult {
  const error = firstModelError(nanoExploreFields(setup.shape), { sizeNm: setup.sizeNm, cutNm: setup.cutNm });
  if (error) throw new Error(error);

  const isCube = setup.shape === 'cube';
  const scaleSizeNm = isCube ? setup.sizeNm : 2 * setup.sizeNm;
  const classification = sizeClass(scaleSizeNm);
  if (!classification) throw new Error('Size must be greater than zero.');
  const sizeLabel = isCube ? 'edge' : 'diameter 2r';
  const rule =
    classification === 'nanoscale'
      ? `${NANOSCALE_MIN_NM} ≤ ${sig(scaleSizeNm)} ≤ ${NANOSCALE_MAX_NM} nm`
      : classification === 'below-nanoscale'
        ? `${sig(scaleSizeNm)} < ${NANOSCALE_MIN_NM} nm`
        : `${sig(scaleSizeNm)} > ${NANOSCALE_MAX_NM} nm`;

  const ratioPerNm = isCube ? cubeSurfaceToVolume(setup.sizeNm) : sphereSurfaceToVolume(setup.sizeNm);
  const steps: WorkedStep[] = [
    {
      id: 'size-class',
      title: 'Size class',
      formula: `Nanoscale is ${NANOSCALE_MIN_NM}–${NANOSCALE_MAX_NM} nm (${sizeLabel})`,
      working: isCube ? `${rule} → ${CLASS_TEXT[classification]}` : `2 × ${sig(setup.sizeNm)} = ${sig(scaleSizeNm)} nm; ${rule} → ${CLASS_TEXT[classification]}`,
      status: 'done',
    },
    {
      id: 'sa-v',
      title: 'Surface area to volume',
      formula: isCube ? 'SA:V = 6l² ÷ l³ = 6 ÷ l' : 'SA:V = 4πr² ÷ (4/3)πr³ = 3 ÷ r',
      working: `SA:V = ${isCube ? 6 : 3} ÷ ${sig(setup.sizeNm)} nm = ${sig(ratioPerNm)} nm⁻¹`,
      status: 'done',
    },
  ];

  let perEdge: number | null = null;
  let limitNote: string | null = null;
  if (!isCube) {
    steps.push({
      id: 'cut',
      title: 'Cut into smaller cubes',
      formula: 'area factor = L ÷ l',
      working: 'Cutting applies to the cube. Switch to the cube to use the cut size.',
      status: 'not-reached',
    });
  } else {
    const factor = subdivisionAreaFactor(setup.sizeNm, setup.cutNm);
    if (!Number.isFinite(factor)) {
      limitNote = `The cut size l (${sig(setup.cutNm)} nm) is larger than the cube edge L (${sig(setup.sizeNm)} nm). Choose l ≤ L.`;
    } else if (!isWhole(factor)) {
      limitNote = `L ÷ l = ${sig(factor)} is not a whole number, so the cube cannot be cut into equal cubes. Choose a cut size that divides the edge exactly.`;
    }
    if (limitNote) {
      steps.push({ id: 'cut', title: 'Cut into smaller cubes', formula: 'area factor = L ÷ l', working: '', status: 'not-reached', note: 'stopped at the model limit' });
    } else {
      perEdge = Math.round(factor);
      const pieces = perEdge ** 3;
      steps.push({
        id: 'cut',
        title: 'Cut into smaller cubes',
        formula: 'area factor = L ÷ l; number of cubes = (L ÷ l)³',
        working: `area factor = ${sig(setup.sizeNm)} ÷ ${sig(setup.cutNm)} = ${sig(perEdge)}; cubes = ${sig(perEdge)}³ = ${sig(pieces)}`,
        status: 'done',
        note: `${sig(perEdge)} × more surface`,
      });
    }
  }

  if (!limitNote && scaleSizeNm > NANO_SCALE_END_NM) {
    limitNote = `${sig(scaleSizeNm)} nm is beyond the 10 µm end of the drawn size scale, so the marker sits at the end. The calculation is unchanged.`;
  }

  return {
    steps,
    scaleSizeNm,
    sizeClassLabel: CLASS_TEXT[classification],
    ratioPerNm,
    perEdge,
    limitNote,
  };
}

/** Input to the teaching tasks: the learner's setup with the result it produced. */
export interface NanoChallengeInput {
  setup: NanoExploreSetup;
  result: NanoExploreResult;
}

export const NANO_QUESTION = 'Why does a material behave differently when its particles are only a few nanometres across?';

/** SA:V of the default 1000 nm cube (6 ÷ 1000 = 0.006 nm⁻¹), doubled for the halving task. */
const DOUBLED_DEFAULT_RATIO_PER_NM = 0.012;
const RATIO_TOLERANCE = 0.05;
const BIG_AREA_FACTOR = 1000;

/** Teaching tasks (CT-INTERACT C7). Each check reads only the setup and the model result. */
export const NANO_CHALLENGES: ExploreChallenge<NanoChallengeInput>[] = [
  {
    id: 'into-nanoscale',
    prompt: 'Shrink the cube into the nanoscale (edge 1–100 nm) and compare its SA:V with the 1000 nm cube.',
    hint: 'Type a cube edge of 100 nm or less.',
    isMet: ({ setup, result }) => setup.shape === 'cube' && result.sizeClassLabel === 'nanoscale',
    why: 'SA:V = 6 ÷ l, so a 20 nm cube has 0.3 nm⁻¹ against 0.006 nm⁻¹ for the 1000 nm cube: 50 times more surface for each unit of volume.',
  },
  {
    id: 'double-ratio',
    prompt: 'Double the SA:V of the 1000 nm cube to 0.012 nm⁻¹ (within 5 %) by changing only the cube size.',
    hint: 'SA:V = 6 ÷ l. Which edge gives 6 ÷ l = 0.012?',
    isMet: ({ setup, result }) =>
      setup.shape === 'cube' &&
      Math.abs(result.ratioPerNm - DOUBLED_DEFAULT_RATIO_PER_NM) <= DOUBLED_DEFAULT_RATIO_PER_NM * RATIO_TOLERANCE,
    why: 'Halving the edge to 500 nm doubles SA:V. The surface shrinks 4 times but the volume shrinks 8 times, so area and volume do not shrink together.',
  },
  {
    id: 'thousand-times',
    prompt: 'Cut a cube into nanoscale pieces that together have at least 1000 times the surface of the whole cube.',
    hint: 'The area factor is L ÷ l, and the cut size l must be 100 nm or less.',
    isMet: ({ setup, result }) =>
      setup.shape === 'cube' && setup.cutNm <= NANOSCALE_MAX_NM && result.perEdge !== null && result.perEdge >= BIG_AREA_FACTOR,
    why: 'Cutting does not change the total volume, but the surface grows by L ÷ l: a 10 000 nm cube cut into 10 nm cubes has 1000 times the surface. This is why nanoparticle catalysts are so reactive.',
  },
];
